import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { AskProvider } from './components/Ask';
import { Loading } from './components/base';
import { ErrorBoundary } from './components/ErrorBoundary';
import Layout from './components/Layout';
import Account from './pages/Account';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';
import { DocumentList, DocumentView } from './pages/Documents';
import Login from './pages/Login';
import ModulePage from './pages/ModulePage';
import ProductDetail from './pages/ProductDetail';
import './index.css';

function AppRoutes() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();

  // Wait for the session check before deciding, or a page refresh would bounce
  // an authenticated user to the login screen.
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center"><Loading /></div>;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Each page inside its own boundary, INSIDE the Layout route, so a page that
  // throws leaves the navigation and the rest of the shell working - the reader
  // can go somewhere else instead of staring at a blank tab. See GP4 in
  // docs/audits/2026-08-10-green-pipeline.md.
  //
  // Keyed on the path, and that is load bearing rather than tidy. A boundary
  // holds its error until it unmounts, and React reconciles the same component
  // type at the same position as the SAME instance - so without the key, one
  // broken page kept showing its error on every page navigated to afterwards.
  // Found by navigating away from a deliberately broken page and watching the
  // error follow. The key forces a remount per route, which clears it.
  const page = (element: ReactNode, label: string) => (
    <ErrorBoundary key={pathname} label={label}>{element}</ErrorBoundary>
  );

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={page(<Dashboard />, 'The dashboard')} />
        <Route path="products/:id" element={page(<ProductDetail />, 'This product')} />
        <Route path="products/:id/manage/:moduleKey" element={page(<ModulePage />, 'This module')} />
        <Route path="documents" element={page(<DocumentList />, 'Documents')} />
        <Route path="documents/:id" element={page(<DocumentView />, 'This document')} />
        <Route path="account" element={page(<Account />, 'Your account')} />
        {user.role === 'owner' && <Route path="admin" element={page(<Admin />, 'Admin')} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      {/* The outer one is the last resort: it catches a throw in the Layout or
          the auth provider, where there is no shell left to preserve. */}
      <ErrorBoundary>
        <AuthProvider>
          <AskProvider>
            <AppRoutes />
          </AskProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
